import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import type { MerchantTheme } from "@sirdab/returns-shared";

import {
  resolveTheme,
  THEME_CSS_PROPERTIES,
  themeCssVars,
  type ResolvedTheme,
} from "./brand";

export interface ThemeContextValue extends ResolvedTheme {
  /** Called once a lookup returns `policy.theme`. Before that, the neutral default applies. */
  applyMerchantTheme: (theme: MerchantTheme | null | undefined) => void;
  displayName: MerchantTheme["displayName"] | null;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Theming propagates as CSS custom properties on `<html>`, so a merchant colour reaches
 * every component (including portalled ones) without a single prop being threaded. The
 * properties are the foundation's own `--primary` family, so every `@sirdab/ui` primitive
 * that paints the action picks the colour up unchanged.
 */
export function ThemeProvider({
  children,
  initialTheme,
}: {
  children: ReactNode;
  initialTheme?: MerchantTheme | null | undefined;
}): React.JSX.Element {
  const [merchantTheme, setMerchantTheme] = useState<MerchantTheme | null>(
    initialTheme ?? null,
  );
  const resolved = useMemo(() => resolveTheme(merchantTheme), [merchantTheme]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const vars = themeCssVars(resolved);
    for (const name of THEME_CSS_PROPERTIES) {
      // No merchant colour means no override: the foundation's ink action stands.
      if (vars === null) root.style.removeProperty(name);
      else root.style.setProperty(name, vars[name]);
    }
  }, [resolved]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;

    if (resolved.pageBackgroundCss === null) {
      root.style.removeProperty("--page-background");
    } else {
      root.style.setProperty("--page-background", resolved.pageBackgroundCss);
    }

    if (resolved.pageInkRgb === null) {
      root.style.removeProperty("--page-ink");
    } else {
      root.style.setProperty("--page-ink", `rgb(${resolved.pageInkRgb})`);
    }
  }, [resolved]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      ...resolved,
      displayName: merchantTheme?.displayName ?? null,
      applyMerchantTheme: (theme) => setMerchantTheme(theme ?? null),
    }),
    [resolved, merchantTheme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (value === null)
    throw new Error("useTheme must be used inside <ThemeProvider>");
  return value;
}
